/**
 * Broker Ladder — click-trading price ladder over the mt-ea bridge (beta).
 *
 * Click the Bids column to work buys, the Asks column to work sells:
 * clicks inside the broker's minimum pending distance send market orders
 * ("trade now"), at-or-better beyond it place limits, through-market places
 * stops. Your working orders appear as lots in bright cells — click to cancel.
 *
 * The ladder renders a 3× viewport buffer of rows and keeps the market
 * vertically centred by scrolling; hovering pauses auto-centre so rows never
 * shift under the cursor mid-click.
 *
 * Data honesty: MT4 CFD feeds expose no market depth, so there are no
 * per-level book sizes. Activity and H/L/O/V accumulate from ticks observed
 * this session — clustering is real, absolute size is not exchange volume.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import clsx from "clsx";
import { cancelWorkingOrder, createOrder, getQuote, OrderType } from "@/shared/api/mt-ea";
import { newIdempotencyKey } from "@/shared/api/mt-ea/idempotency";
import { AutoSwitchMode, useBrokerStore } from "@/stores/BrokerStore";
import { useBrokerWidgetSymbol } from "@/hooks/useBrokerWidgetSymbol";

const ROW_H = 20; // px, must match the row height classes below
const QTY_PRESETS = [0.1, 0.5, 1, 5, 10];
const STEP_CHOICES = ["auto", "0.0001", "0.001", "0.01", "0.05", "0.25", "1"] as const;

/** Desired row granularity before snapping to the broker's tick grid. */
const targetStep = (price: number): number => {
  if (price >= 10000) return 1;
  if (price >= 1000) return 0.25;
  if (price >= 100) return 0.05;
  if (price >= 10) return 0.01;
  if (price >= 2) return 0.001;
  return 0.0001;
};

/**
 * Row step must be a whole number of broker ticks — an off-grid price is
 * rejected as "invalid stops" no matter how far from the market it sits.
 * Picks the smallest 1/2/5/10… multiple of the tick that meets the target.
 */
const snapStepToTick = (target: number, tick: number | null | undefined): number => {
  if (!tick || tick <= 0) return target;
  for (const multiple of [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000]) {
    const candidate = parseFloat((tick * multiple).toPrecision(12));
    if (candidate >= target - 1e-12) return candidate;
  }
  return tick;
};

/** Decimals needed to represent a step exactly (0.25 → 2, not 1). */
const decimalsFor = (step: number) => {
  const text = step.toFixed(10).replace(/0+$/, "");
  const dot = text.indexOf(".");
  return dot < 0 ? 0 : text.length - dot - 1;
};

const BrokerLadder = ({ widgetCompositeId }: { widgetCompositeId?: string }) => {
  const {
    account,
    accountLoaded,
    positions,
    workingOrders,
    lastCommand,
    connect,
    disconnect,
    symbolMap,
    quoteFor,
    brokerSymbolFor,
  } = useBrokerStore();
  // Per widget instance, so several ladders can watch different assets.
  const { activeSymbol, setActiveSymbol, autoSwitchMode, setAutoSwitchMode } =
    useBrokerWidgetSymbol(widgetCompositeId);

  const [qty, setQty] = useState(0.1);
  const [stepChoice, setStepChoice] = useState<(typeof STEP_CHOICES)[number]>("auto");
  const [center, setCenter] = useState<number | null>(null);
  const [held, setHeld] = useState(false);
  const [viewportRows, setViewportRows] = useState(24);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const vapRef = useRef<Map<number, number>>(new Map());
  const statsRef = useRef<{ open: number | null; high: number | null; low: number | null; ticks: number }>({
    open: null,
    high: null,
    low: null,
    ticks: 0,
  });
  const [vapVersion, setVapVersion] = useState(0);
  const myCommands = useRef<Set<number>>(new Set());

  useEffect(() => {
    connect();
    return () => disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the server-side quote watch alive for the active symbol.
  useEffect(() => {
    if (!account) return;
    const register = () => getQuote(activeSymbol).catch(() => undefined);
    register();
    const interval = setInterval(register, 120_000);
    return () => clearInterval(interval);
  }, [account, activeSymbol]);

  // Fit the row count to the widget's real height.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => {
      setViewportRows(Math.max(12, Math.floor(el.clientHeight / ROW_H)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [account]);

  const quote = quoteFor(activeSymbol);
  const brokerSymbol = brokerSymbolFor(activeSymbol);
  const mid = quote ? (quote.bid + quote.ask) / 2 : null;
  const tick = quote?.tick_size ?? null;
  const step = snapStepToTick(
    stepChoice === "auto" ? (mid ? targetStep(mid) : 0.0001) : parseFloat(stepChoice),
    tick
  );
  // Show at least the broker's own precision, and always enough for the step.
  const decimals = Math.max(decimalsFor(step), quote?.digits ?? 0);
  // One normalization for every bucket key — raw float arithmetic produces
  // keys like 1.1527400000000002 that never match the row prices.
  const bucketize = (price: number) =>
    parseFloat((Math.round(price / step) * step).toFixed(Math.max(decimals, 8)));

  // Reset activity + session stats + recenter when instrument or step changes.
  useEffect(() => {
    vapRef.current = new Map();
    statsRef.current = { open: null, high: null, low: null, ticks: 0 };
    setCenter(null);
    setVapVersion((v) => v + 1);
  }, [activeSymbol, step]);

  // Accumulate tick activity + session stats.
  useEffect(() => {
    if (mid == null) return;
    const bucket = bucketize(mid);
    vapRef.current.set(bucket, (vapRef.current.get(bucket) ?? 0) + 1);
    const stats = statsRef.current;
    if (stats.open == null) stats.open = mid;
    stats.high = stats.high == null ? mid : Math.max(stats.high, mid);
    stats.low = stats.low == null ? mid : Math.min(stats.low, mid);
    stats.ticks += 1;
    setVapVersion((v) => v + 1);
  }, [quote?.bid, quote?.ask]); // eslint-disable-line react-hooks/exhaustive-deps

  // Anchor management: snap on first quote; re-anchor when the market drifts
  // far from the buffer centre (unless the user is hovering).
  useEffect(() => {
    if (mid == null) return;
    const snapped = bucketize(mid);
    if (center == null) {
      setCenter(snapped);
      return;
    }
    if (!held && Math.abs(snapped - center) > (viewportRows / 2) * step) {
      setCenter(snapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mid, step, center, held, viewportRows]);

  // 3× viewport buffer of rows around the anchor.
  const totalRows = viewportRows * 3;
  const rows = useMemo(() => {
    if (center == null) return [];
    const half = Math.floor(totalRows / 2);
    const out: number[] = [];
    for (let i = half; i >= -half; i--) {
      // 8dp keeps the value on the tick grid; `decimals` is display only
      out.push(parseFloat((center + i * step).toFixed(8)));
    }
    return out;
  }, [center, step, decimals, totalRows]);

  // Keep the market centred in the viewport by scrolling the buffer —
  // paused while the user hovers so rows don't shift under the cursor.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || held || mid == null || center == null || rows.length === 0) return;
    const midIndex = rows.findIndex((p) => p === bucketize(mid));
    const target =
      (midIndex >= 0 ? midIndex : Math.floor(rows.length / 2)) * ROW_H -
      el.clientHeight / 2 +
      ROW_H / 2;
    el.scrollTop = Math.max(0, target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, mid, held, viewportRows]);

  const maxVap = useMemo(() => {
    let max = 0;
    vapRef.current.forEach((count) => (max = Math.max(max, count)));
    return max;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vapVersion]);

  const ordersAt = useMemo(() => {
    const map = new Map<number, { buys: typeof workingOrders; sells: typeof workingOrders }>();
    workingOrders
      .filter((o) => o.symbol === brokerSymbol)
      .forEach((o) => {
        const bucket = bucketize(o.price);
        const entry = map.get(bucket) ?? { buys: [], sells: [] };
        (o.type.startsWith("buy") ? entry.buys : entry.sells).push(o);
        map.set(bucket, entry);
      });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingOrders, brokerSymbol, step, decimals]);

  const myBuys = workingOrders.filter((o) => o.symbol === brokerSymbol && o.type.startsWith("buy"));
  const mySells = workingOrders.filter((o) => o.symbol === brokerSymbol && o.type.startsWith("sell"));

  const symbolPositions = positions.filter((p) => p.symbol === brokerSymbol);
  const positionRow =
    symbolPositions.length > 0 ? bucketize(symbolPositions[0].open_price) : null;
  const positionPl = symbolPositions.reduce((sum, p) => sum + p.profit, 0);
  const positionLots = symbolPositions.reduce((sum, p) => sum + p.lots, 0);

  const disabled = !account?.connected || account.kill_switch || account.live_blocked;

  // Watch fills/rejects for orders submitted from this ladder.
  useEffect(() => {
    if (!lastCommand || !myCommands.current.has(lastCommand.id)) return;
    myCommands.current.delete(lastCommand.id);
    const what = `${lastCommand.type.replace("_", " ")} ${lastCommand.lots ?? ""} ${activeSymbol}`;
    if (lastCommand.status === "filled") {
      toast.success(
        lastCommand.type.startsWith("market") || lastCommand.type === "close_position"
          ? `Filled: ${what} @ ${lastCommand.result_price}`
          : lastCommand.type === "cancel_order"
            ? `Cancelled #${lastCommand.position_ticket}`
            : `Working: ${what} @ ${lastCommand.price ?? lastCommand.result_price}`
      );
    } else if (lastCommand.status !== "pending" && lastCommand.status !== "delivered") {
      toast.error(`${lastCommand.status}: ${what} — ${lastCommand.error_text || "no reason"}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCommand]);

  /** MT brokers refuse pending orders within stop_dist of the market. Clicks
   *  inside that band mean "trade now" → market order; outside it, limits
   *  rest at-or-better and stops rest through-market. */
  const orderTypeFor = (side: "buy" | "sell", price: number): OrderType => {
    if (!quote) return side === "buy" ? "market_buy" : "market_sell";
    // Some brokers report stop level 0 but still enforce a minimum distance
    // server-side — fall back to 2× spread when nothing is reported.
    const dist =
      quote.stop_dist && quote.stop_dist > 0
        ? quote.stop_dist
        : (quote.ask - quote.bid) * 2;
    if (side === "buy") {
      if (price <= quote.bid - dist) return "limit_buy";
      if (price >= quote.ask + dist) return "stop_buy";
      return "market_buy";
    }
    if (price >= quote.ask + dist) return "limit_sell";
    if (price <= quote.bid - dist) return "stop_sell";
    return "market_sell";
  };

  const clickLabel = (side: "buy" | "sell", price: number) => {
    const type = orderTypeFor(side, price);
    const verb = type.startsWith("market") ? "market" : type.startsWith("limit") ? "limit" : "stop";
    return `${side === "buy" ? "Buy" : "Sell"} ${verb} ${qty}${verb === "market" ? "" : ` @ ${price.toFixed(decimals)}`}`;
  };

  const place = async (side: "buy" | "sell", price: number) => {
    if (disabled || !quote) return;
    const type = orderTypeFor(side, price);
    try {
      const command = await createOrder({
        idempotency_key: newIdempotencyKey(),
        type,
        symbol: activeSymbol,
        lots: qty,
        price: type.startsWith("market") ? undefined : price,
      });
      myCommands.current.add(command.id);
    } catch (error: any) {
      toast.error(error?.response?.data?.detail || "Order failed to submit");
    }
  };

  const cancel = async (tickets: number[]) => {
    for (const ticket of tickets) {
      try {
        const command = await cancelWorkingOrder(ticket, newIdempotencyKey());
        myCommands.current.add(command.id);
      } catch (error: any) {
        toast.error(error?.response?.data?.detail || `Cancel #${ticket} failed`);
      }
    }
  };

  const tradableSymbols = useMemo(
    () =>
      Object.entries(symbolMap)
        .filter(([, broker]) => broker !== null)
        .map(([canonical]) => canonical)
        .sort(),
    [symbolMap]
  );

  if (!accountLoaded) {
    return <div className="p-4 text-xs text-gray-400">Loading broker connection…</div>;
  }
  if (!account) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Connect your MetaTrader account to use the ladder.
        </p>
        <Link
          to="/settings/broker"
          className="rounded-md bg-squawk-blue px-3 py-1.5 text-xs font-semibold text-white hover:bg-squawk-blue-light transition"
        >
          Connect a broker
        </Link>
      </div>
    );
  }

  const bidBucket = quote ? bucketize(quote.bid) : null;
  const askBucket = quote ? bucketize(quote.ask) : null;
  const stats = statsRef.current;
  const nearMarket = (price: number) =>
    bidBucket != null &&
    askBucket != null &&
    price >= bidBucket - 5 * step &&
    price <= askBucket + 5 * step;

  const railButton =
    "w-full rounded text-2xs py-1 font-semibold transition disabled:opacity-40 disabled:cursor-default";

  return (
    <div className="flex h-full bg-gray-800 text-white text-2xs overflow-hidden">
      {/* Control rail — functional controls only */}
      <div className="w-24 shrink-0 bg-gray-900 px-1.5 py-2 flex flex-col gap-3 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-600">
        <div>
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">Symbol</div>
          <select
            value={activeSymbol}
            onChange={(e) => setActiveSymbol(e.target.value)}
            className="w-full bg-gray-800 rounded text-2xs font-semibold p-1 border-0 focus:ring-1 focus:ring-squawk-blue"
          >
            {!tradableSymbols.includes(activeSymbol) && (
              <option value={activeSymbol}>{activeSymbol}</option>
            )}
            {tradableSymbols.map((symbol) => (
              <option key={symbol} value={symbol}>
                {symbol}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">Size (lots)</div>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={qty}
            onChange={(e) => setQty(Math.max(0.01, parseFloat(e.target.value) || 0.01))}
            className="w-full bg-blue-700 text-white text-2xs font-bold text-center py-1 rounded border-0 focus:ring-1 focus:ring-blue-400 tabular-nums mb-1"
          />
          <div className="grid grid-cols-2 gap-1">
            {QTY_PRESETS.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => setQty(preset)}
                className={clsx(
                  "rounded text-2xs py-1 tabular-nums transition",
                  qty === preset
                    ? "bg-blue-700 font-bold"
                    : "bg-gray-700 hover:bg-gray-600"
                )}
              >
                {preset}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setQty(0.1)}
              className="rounded text-2xs py-1 bg-gray-700 hover:bg-gray-600 transition"
            >
              CLR
            </button>
          </div>
        </div>

        <div>
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">
            Auto-switch
          </div>
          <select
            value={autoSwitchMode}
            onChange={(e) => setAutoSwitchMode(e.target.value as AutoSwitchMode)}
            title="Let headline tags change this ladder's asset"
            className="w-full bg-gray-800 rounded text-2xs p-1 border-0 focus:ring-1 focus:ring-squawk-blue"
          >
            <option value="off">Off</option>
            <option value="click">On click</option>
            <option value="feed">Follow feed</option>
          </select>
        </div>

        <div>
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">Ladder</div>
          <select
            value={stepChoice}
            onChange={(e) => setStepChoice(e.target.value as (typeof STEP_CHOICES)[number])}
            title="Price step per row"
            className="w-full bg-gray-800 rounded text-2xs p-1 border-0 focus:ring-1 focus:ring-squawk-blue mb-1"
          >
            {STEP_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {choice === "auto" ? "auto step" : choice}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => mid != null && setCenter(bucketize(mid))}
            className={clsx(railButton, "bg-gray-700 hover:bg-gray-600")}
          >
            Center
          </button>
          <div
            className={clsx(
              "mt-1 text-center text-[9px] transition-opacity",
              held ? "text-amber-400 opacity-100" : "opacity-0"
            )}
          >
            hold — auto-centre paused
          </div>
        </div>

        <div className="mt-auto">
          <div className="text-[9px] uppercase tracking-wider text-gray-500 mb-1">
            Working orders
          </div>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              disabled={myBuys.length === 0}
              onClick={() => cancel(myBuys.map((o) => o.ticket))}
              className={clsx(railButton, "bg-blue-700 hover:bg-blue-600")}
            >
              CXL B{myBuys.length > 0 ? ` (${myBuys.length})` : ""}
            </button>
            <button
              type="button"
              disabled={mySells.length === 0}
              onClick={() => cancel(mySells.map((o) => o.ticket))}
              className={clsx(railButton, "bg-red-700 hover:bg-red-600")}
            >
              CXL S{mySells.length > 0 ? ` (${mySells.length})` : ""}
            </button>
            <button
              type="button"
              disabled={myBuys.length + mySells.length === 0}
              onClick={() => cancel([...myBuys, ...mySells].map((o) => o.ticket))}
              className={clsx(railButton, "bg-gray-600 hover:bg-gray-500")}
            >
              CXL All{myBuys.length + mySells.length > 0 ? ` (${myBuys.length + mySells.length})` : ""}
            </button>
          </div>
        </div>
      </div>

      {/* Main ladder section */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header with P/L and session H/L/O/V */}
        <div className="flex justify-between items-center bg-gray-700 px-2 py-1.5 text-2xs font-semibold shrink-0">
          <span
            className={clsx(
              symbolPositions.length === 0
                ? "text-gray-400"
                : positionPl >= 0
                  ? "text-green-400"
                  : "text-red-400"
            )}
          >
            {symbolPositions.length > 0
              ? `${symbolPositions[0].side === "buy" ? "+" : "−"}${positionLots.toFixed(2)}  ${positionPl >= 0 ? "+" : ""}${positionPl.toFixed(2)}`
              : "flat"}
          </span>
          <div className="text-right leading-tight">
            <p>
              H: <span className="text-green-400">{stats.high?.toFixed(decimals) ?? "—"}</span>{" "}
              O: <span className="text-white">{stats.open?.toFixed(decimals) ?? "—"}</span>
            </p>
            <p>
              L: <span className="text-red-400">{stats.low?.toFixed(decimals) ?? "—"}</span>{" "}
              V: <span className="text-white">{stats.ticks}</span>
            </p>
          </div>
        </div>

        {disabled && (
          <div className="bg-gray-700/60 px-2 py-1 text-2xs text-amber-400 shrink-0">
            {account.kill_switch
              ? "Trading paused by Newsquawk support."
              : account.live_blocked
                ? "Live account — enable live trading in Settings → Broker."
                : "Terminal offline — ladder is view-only."}
          </div>
        )}

        {/* Column headers */}
        <div className="grid grid-cols-[1fr_1.2fr_1fr_1.2fr] bg-gray-700 text-2xs font-semibold text-center shrink-0 py-0.5">
          <span>Bids</span>
          <span>Price</span>
          <span>Asks</span>
          <span title="Tick activity observed this session — not exchange volume">Activity*</span>
        </div>

        {/* Ladder rows — 3× buffer, market kept centred by scroll */}
        <div
          ref={scrollRef}
          onMouseEnter={() => setHeld(true)}
          onMouseLeave={() => setHeld(false)}
          className="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-500"
        >
          {rows.map((price, index) => {
            const own = ordersAt.get(price);
            const isBid = price === bidBucket;
            const isAsk = price === askBucket;
            const near = nearMarket(price);
            const vap = vapRef.current.get(price) ?? 0;
            const ownBuyLots = own?.buys.reduce((s, o) => s + o.lots, 0) ?? 0;
            const ownSellLots = own?.sells.reduce((s, o) => s + o.lots, 0) ?? 0;
            return (
              <div
                key={price}
                className={clsx(
                  "grid grid-cols-[1fr_1.2fr_1fr_1.2fr] h-[20px] items-stretch text-center tabular-nums",
                  index % 2 === 0 ? "bg-gray-800" : "bg-gray-900"
                )}
              >
                {/* Bids */}
                <div
                  onClick={() =>
                    disabled
                      ? undefined
                      : ownBuyLots > 0
                        ? cancel(own!.buys.map((o) => o.ticket))
                        : place("buy", price)
                  }
                  title={ownBuyLots > 0 ? "Working buy — click to cancel" : clickLabel("buy", price)}
                  className={clsx(
                    "flex items-center justify-center border border-gray-950 select-none",
                    isBid || ownBuyLots > 0 ? "bg-blue-600 font-bold" : near ? "bg-blue-700" : "bg-blue-800",
                    !disabled && "cursor-pointer hover:brightness-125",
                    disabled && "opacity-50"
                  )}
                >
                  {ownBuyLots > 0 ? ownBuyLots.toFixed(2) : isBid ? quote?.bid.toFixed(decimals) : ""}
                </div>
                {/* Price */}
                <div
                  className={clsx(
                    "flex items-center justify-center",
                    positionRow === price
                      ? "text-amber-400 font-bold"
                      : isBid || isAsk
                        ? "text-white font-bold"
                        : "text-gray-200"
                  )}
                >
                  {positionRow === price && (
                    <span className="mr-1">{symbolPositions[0]?.side === "buy" ? "▲" : "▼"}</span>
                  )}
                  {price.toFixed(decimals)}
                </div>
                {/* Asks */}
                <div
                  onClick={() =>
                    disabled
                      ? undefined
                      : ownSellLots > 0
                        ? cancel(own!.sells.map((o) => o.ticket))
                        : place("sell", price)
                  }
                  title={ownSellLots > 0 ? "Working sell — click to cancel" : clickLabel("sell", price)}
                  className={clsx(
                    "flex items-center justify-center border border-gray-950 select-none",
                    isAsk || ownSellLots > 0 ? "bg-red-600 font-bold" : near ? "bg-red-700" : "bg-red-800",
                    !disabled && "cursor-pointer hover:brightness-125",
                    disabled && "opacity-50"
                  )}
                >
                  {ownSellLots > 0 ? ownSellLots.toFixed(2) : isAsk ? quote?.ask.toFixed(decimals) : ""}
                </div>
                {/* Activity */}
                <div className="flex items-center px-1">
                  {vap > 0 && maxVap > 0 && (
                    <div
                      className="h-2 bg-gray-400"
                      style={{ width: `${Math.max(6, (vap / maxVap) * 70)}%` }}
                    />
                  )}
                  {vap > 0 && <span className="ml-1.5">{vap}</span>}
                </div>
              </div>
            );
          })}
          {rows.length === 0 && (
            <div className="p-4 text-center text-gray-400">Waiting for quotes…</div>
          )}
        </div>

        <div className="bg-gray-900 px-2 py-0.5 text-[9px] text-gray-500 shrink-0">
          *Activity/V = tick counts observed this session, not exchange volume (CFD feeds carry no depth).
        </div>
      </div>
    </div>
  );
};

export default BrokerLadder;
